import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { decodeQr } from './moic/qr';
import { parseVerifyUrl } from './moic/verifyUrl';
import { verifyCertificate } from './moic/api';
import { certificateFields } from './moic/fields';
import type { CertificateFields } from './moic/fields';

const MSG = {
  unreadable: 'ອ່ານຮູບບໍ່ໄດ້ — ຮອງຮັບສະເພາະໄຟລ໌ຮູບ',
  noQr: 'ບໍ່ພົບ QR ໃນຮູບ — ລອງຖ່າຍໃໝ່ໃຫ້ຊັດ ຫຼື ພິມຂໍ້ມູນເອງ',
  foreignQr: 'QR ນີ້ບໍ່ແມ່ນຂອງລະບົບທະບຽນວິສາຫະກິດ',
  offline: 'ຕິດຕໍ່ລະບົບທະບຽນບໍ່ໄດ້ — ພິມຂໍ້ມູນເອງໄດ້ຕາມປົກກະຕິ',
  invalid_signature: 'ລາຍເຊັນບໍ່ຖືກຕ້ອງ — ໃບທະບຽນນີ້ອາດບໍ່ແທ້',
  registry_error: 'ລະບົບທະບຽນແຈ້ງວ່າບໍ່ພົບຂໍ້ມູນຂອງໃບນີ້',
  no_names: 'ຂໍ້ມູນທີ່ໄດ້ຮັບບໍ່ມີຊື່ວິສາຫະກິດ',
};

type Status =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'filled'; stale: boolean }
  | { kind: 'error'; message: string };

/** Reads the QR on a ໃບທະບຽນວິສາຫະກິດ and hands the registered names and TIN to
 *  the form. Every failure is a message and nothing else: the operator can
 *  always ignore this and type the fields, so nothing here may block the form. */
export function CertificateDropzone({ onFill }: { onFill: (fields: CertificateFields) => void }) {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [over, setOver] = useState(false);
  const latest = useRef(0);

  const read = useCallback(async (file: Blob) => {
    // Scans can overlap — pasting and dropping are not gated on the busy state —
    // and the newest must win. An earlier scan landing last would fill the form
    // with one company while the message names another, and pathName is derived
    // from nameEn and is permanent.
    const run = (latest.current += 1);
    const current = () => run === latest.current;
    setStatus({ kind: 'busy' });

    let payload: string | null;
    try {
      payload = await decodeQr(file);
    } catch {
      if (current()) setStatus({ kind: 'error', message: MSG.unreadable });
      return;
    }
    if (!current()) return;
    if (payload === null) {
      setStatus({ kind: 'error', message: MSG.noQr });
      return;
    }

    // Pinned to the registry's own host — see verifyUrl.ts. A QR that points
    // anywhere else is never fetched.
    const params = parseVerifyUrl(payload);
    if (params === null) {
      setStatus({ kind: 'error', message: MSG.foreignQr });
      return;
    }

    let res;
    try {
      res = await verifyCertificate(params);
    } catch {
      if (current()) setStatus({ kind: 'error', message: MSG.offline });
      return;
    }
    if (!current()) return;

    const result = certificateFields(res);
    if (!result.ok) {
      setStatus({ kind: 'error', message: MSG[result.reason] });
      return;
    }
    onFill(result.fields);
    setStatus({ kind: 'filled', stale: result.stale });
  }, [onFill]);

  // Pasting a screenshot is how most of these arrive — the certificate is
  // usually a PDF on screen, not a photograph.
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const image = Array.from(e.clipboardData?.items ?? []).find((i) => i.type.startsWith('image/'));
      const file = image?.getAsFile();
      if (file != null) {
        e.preventDefault();
        void read(file);
      }
    }
    document.addEventListener('paste', onPaste);
    return () => { document.removeEventListener('paste', onPaste); };
  }, [read]);

  return (
    <Box
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => { setOver(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const file = e.dataTransfer.files[0];
        if (file != null) void read(file);
      }}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px dashed',
        borderColor: over ? 'primary.main' : 'divider',
        bgcolor: over ? 'action.hover' : 'transparent',
        transition: 'border-color .15s, background-color .15s',
      }}
    >
      <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            component="label"
            variant="outlined"
            size="small"
            startIcon={status.kind === 'busy' ? <CircularProgress size={16} /> : <QrCodeScannerIcon />}
            disabled={status.kind === 'busy'}
          >
            ອ່ານຈາກໃບທະບຽນ
            <input
              type="file"
              accept="image/*"
              hidden
              aria-label="ຮູບໃບທະບຽນວິສາຫະກິດ"
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Cleared so choosing the same file twice still fires a change.
                e.target.value = '';
                if (file != null) void read(file);
              }}
            />
          </Button>
          <Typography variant="caption" color="text.secondary">
            ອັບໂຫຼດ ຫຼື ລາກຮູບມາວາງ ຫຼື ກົດ Ctrl+V — ຈະອ່ານ QR ແລ້ວຕື່ມຊື່ ແລະ TIN ໃຫ້
          </Typography>
        </Stack>

        {status.kind === 'error' && (
          <Alert severity="warning" sx={{ width: '100%' }}>{status.message}</Alert>
        )}
        {status.kind === 'filled' && (
          <Alert severity={status.stale ? 'warning' : 'success'} sx={{ width: '100%' }}>
            {status.stale
              ? 'ຕື່ມຂໍ້ມູນແລ້ວ ແຕ່ທະບຽນມີການແກ້ໄຂຫຼັງອອກໃບນີ້ — ກວດຄືນກ່ອນສ້າງ'
              : 'ຕື່ມຂໍ້ມູນຈາກໃບທະບຽນແລ້ວ — ກວດຄືນກ່ອນສ້າງ'}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
